import React, { useContext, useState } from "react";
import {
  Button,
  Stack,
  Text,
  Box,
  Loader,
  Center,
  useMantineTheme,
  Group,
  Image,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconShieldCheck } from "@tabler/icons-react";
import { DataContext } from "../../../context/DataContext";
import useStatusCheck from "../../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../../hooks/useEdgeStatusCheck";
import useLocale from "../../../hooks/useLocale";
import { getEdgeQueueUrl } from "../../../utils/edge";
import FloatingLabelInput from "../../../components/floating-label-input";
import { validateDob } from "../../../utils/validateDob";
import CpfLogo from "../../../assets/cpf.png";

// Strip formatting from a CPF (e.g. "005.702.011-60" -> "00570201160")
const cleanCpf = (value) => String(value || "").replace(/\D/g, "");

// Progressive display mask: 000.000.000-00
const formatCpf = (value) => {
  const digits = cleanCpf(value).slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9)
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

// Strip formatting from a DOB typed as DD/MM/YYYY (Brazilian order)
const cleanDob = (value) => String(value || "").replace(/\D/g, "").slice(0, 8);

// Progressive display mask: DD/MM/YYYY
const formatDob = (value) => {
  const digits = cleanDob(value);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

// Reorder DD/MM/YYYY input digits to the platform's YYYYMMDD wire format
const dobToYmd = (value) => {
  const digits = cleanDob(value);
  if (digits.length !== 8) return null;
  return `${digits.slice(4)}${digits.slice(2, 4)}${digits.slice(0, 2)}`;
};

// Full CPF structural validation (mod-11 check digits)
const isValidCpf = (value) => {
  const cpf = cleanCpf(value);
  if (cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false; // all same digit

  const digits = cpf.split("").map(Number);
  for (const position of [9, 10]) {
    let sum = 0;
    for (let i = 0; i < position; i++) {
      sum += digits[i] * (position + 1 - i);
    }
    const expected = ((sum * 10) % 11) % 10;
    if (digits[position] !== expected) return false;
  }
  return true;
};

const BrazilCpfStart = () => {
  const { settings, data, dispatch } = useContext(DataContext);
  const theme = useMantineTheme();
  const locale = useLocale("brazil-cpf");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const isEdgeMode = data?.edge === true;

  // Poll for the verification result once the CPF has been submitted.
  // The default handlers dispatch PASS / FAIL / NEXT_APP_TRIGGER into
  // the journey engine for us.
  useStatusCheck(!isEdgeMode && submitted, false, "dcams/brazil-cpf");
  useEdgeStatusCheck(isEdgeMode && submitted);

  const form = useForm({
    initialValues: {
      cpf: "",
      dob: "",
    },
    validate: {
      cpf: (value) => (isValidCpf(value) ? null : locale.form.cpfError),
      dob: (value) =>
        validateDob(dobToYmd(value) || "", locale.form.dobError),
    },
  });

  const handleSubmit = async (values) => {
    setError(null);
    setSubmitting(true);

    const requestId = settings?.request_id || data?.request_id;
    const dob = dobToYmd(values.dob);
    const payload = {
      ...data,
      request_id: requestId,
      cpf: cleanCpf(values.cpf),
      dob,
      queue: "brazil-cpf",
    };

    // Keep the DOB in the shared data context so later journey steps
    // (and retries) see it alongside the rest of the session data.
    dispatch({ type: "SET_DATA", payload: { ...data, dob } });

    try {
      const response = await fetch(getEdgeQueueUrl(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("Brazil CPF submission response:", result);

      if (result.success === false) {
        throw new Error(result.message || "Submission rejected");
      }

      dispatch({ type: "SET_STATUS", payload: "PENDING" });
      setSubmitted(true);
    } catch (err) {
      console.error("Error submitting CPF:", err);
      setError(locale.errors.startingVerification);
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <Center style={{ height: "100%" }}>
        <Stack align="center">
          <Text color="red">{error}</Text>
          <Button
            onClick={() => {
              setError(null);
              setSubmitting(false);
              setSubmitted(false);
            }}
          >
            {locale.button.tryAgain}
          </Button>
        </Stack>
      </Center>
    );
  }

  // Waiting for the verification result
  if (submitted) {
    return (
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
        px="md"
        pt="md"
      >
        <Image src={CpfLogo} width={76} alt="CPF Brasil" />
        <Center style={{ flexGrow: 1 }}>
          <Stack align="center">
            <Loader size="lg" color={theme.primaryColor} />
            <Text c="dimmed">{locale.loading.verifying}</Text>
          </Stack>
        </Center>
      </Box>
    );
  }

  return (
    <form
      onSubmit={form.onSubmit(handleSubmit)}
      style={{ height: "100%", display: "flex" }}
    >
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          width: "100%",
        }}
        px="md"
        pt="md"
      >
        {/* Logo, heading, and CPF input */}
        <Box>
          <Image src={CpfLogo} width={76} alt="CPF Brasil" mb="lg" />

          <Text fz={24} fw={800} lh={1.25} mb={8}>
            {locale.header.title}
          </Text>
          <Text fz="sm" c="dimmed" mb="xl">
            {locale.header.subtitle}
          </Text>

          <FloatingLabelInput
            label={locale.form.cpfLabel}
            placeholder="000.000.000-00"
            inputMode="numeric"
            maxLength={14}
            disabled={submitting}
            value={form.values.cpf}
            error={form.errors.cpf}
            customOnChange={(event) =>
              form.setFieldValue("cpf", formatCpf(event.currentTarget.value))
            }
          />

          <Box mt="md">
            <FloatingLabelInput
              label={locale.form.dobLabel}
              placeholder={locale.form.dobPlaceholder}
              inputMode="numeric"
              maxLength={10}
              disabled={submitting}
              value={form.values.dob}
              error={form.errors.dob}
              customOnChange={(event) =>
                form.setFieldValue("dob", formatDob(event.currentTarget.value))
              }
            />
          </Box>
        </Box>

        {/* Trust marker and submit */}
        <Box pb="sm">
          <Group noWrap spacing={6} position="center" mb="md">
            <IconShieldCheck size={16} />
            <Text fz="xs" c="dimmed">
              {locale.features.trustLine}
            </Text>
          </Group>

          <Button type="submit" size="lg" fullWidth loading={submitting}>
            {locale.button.verify}
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default BrazilCpfStart;
